import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import TrendingSidebar from '@/pages/home/components/TrendingSidebar';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/news';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-20 px-4 md:px-6">
            <div className="text-center py-20">
              <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
              <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除。</p>
              <Link to="/" className="inline-flex px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer">
                返回首页
              </Link>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '51黑料',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.51heilia.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: article.category, path: `/category/${article.categorySlug}` },
              { name: article.title },
            ]}
          />

          <main className="w-full px-4 md:px-6 pb-14">
            <div className="flex flex-col lg:flex-row gap-8">
              <article className="flex-1 min-w-0 max-w-3xl">
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Link
                      to={`/category/${article.categorySlug}`}
                      className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors cursor-pointer"
                    >
                      {article.category}
                    </Link>
                    {article.isHot && (
                      <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                        热门
                      </span>
                    )}
                  </div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4">
                    {article.title}
                  </h1>
                  <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-user-line"></i>
                      </div>
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-calendar-line"></i>
                      </div>
                      {article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-eye-line"></i>
                      </div>
                      {article.views.toLocaleString()} 阅读
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-chat-3-line"></i>
                      </div>
                      {article.comments.toLocaleString()} 评论
                    </span>
                  </div>
                </div>

                <div className="relative h-[400px] md:h-[480px] rounded-lg overflow-hidden mb-8">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover"
                    title={`${article.title} - 51黑料网`}
                  />
                </div>

                <div
                  className="article-content"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="mt-8 pt-6 border-t border-background-200">
                  <div className="flex flex-wrap gap-2 mb-6">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium cursor-pointer hover:bg-secondary-200 transition-colors"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="text-sm text-foreground-600">分享到：</span>
                    <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer" aria-label="分享到微信" rel="nofollow">
                      <i className="ri-wechat-line"></i>
                    </a>
                    <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer" aria-label="分享到微博" rel="nofollow">
                      <i className="ri-weibo-line"></i>
                    </a>
                    <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer" aria-label="复制链接" rel="nofollow">
                      <i className="ri-link"></i>
                    </a>
                  </div>
                </div>

                {relatedArticles.length > 0 && (
                  <section className="mt-10 pt-8 border-t border-background-200">
                    <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-5">
                      相关文章
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {relatedArticles.map((related) => (
                        <Link
                          key={related.id}
                          to={`/article/${related.slug}`}
                          className="group flex gap-3 p-3 rounded-lg border border-background-200 hover:border-primary-200 transition-all cursor-pointer"
                        >
                          <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
                            <img
                              src={related.imageUrl}
                              alt={related.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs text-primary-500">{related.category}</span>
                            <h3 className="text-sm font-medium text-foreground-800 line-clamp-2 mt-1 group-hover:text-primary-500 transition-colors">
                              {related.title}
                            </h3>
                            <p className="text-xs text-secondary-500 mt-1">{related.date}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
              </article>

              <TrendingSidebar />
            </div>
          </main>
        </div>

        <Footer />
      </div>
    </>
  );
}